import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../sub-components/Laoder";
import { useNavigate } from "react-router-dom";
interface FormValues {
  email: string;
}

interface FormErrors {
  email?: string;
}

interface ResetFormValues {
  password: string;
  confirmPassword: string;
}

interface ResetFormErrors {
  password?: string;
  confirmPassword?: string;
}

type CodeState = {
  [key: string]: string;
};

export default function Password() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [otpFiled, setOtpFiled] = useState(false);
  const [passwordFields, changePasswordFields] = useState(false);
  const [codes, setCodes] = useState<CodeState>({
    code1: "",
    code2: "",
    code3: "",
    code4: "",
    code5: "",
    code6: "",
  });
  const [timer, setTimer] = useState(300); // 5 minutes
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  const emailFormik = useFormik<FormValues>({
    initialValues: { email: "" },
    validate: (values) => {
      const errors: FormErrors = {};
      if (!values.email) {
        errors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(values.email)) {
        errors.email = "Invalid email address";
      }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/forgot-password`,
          { email: values.email }
        );

        sessionStorage.setItem("user-email", values.email);
        toast.success(response.data.message);
        setOtpFiled(true);
        startTimer(); // Start the timer when OTP is sent
      } catch (error) {
        toast.error("Failed to send OTP. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const startTimer = () => {
    const id = setInterval(() => {
      setTimer((prevTime) => {
        if (prevTime <= 0) {
          clearInterval(id);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000); // Update every second

    setIntervalId(id);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    if (/^\d?$/.test(value)) {
      setCodes((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otp = Object.values(codes).join("");
    const email = sessionStorage.getItem("user-email");

    if (otp.length !== 6 || !email) {
      toast.error("Invalid OTP or email missing.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/verify-otp`,
        { email, otp }
      );
      toast.success(response.data.message);
      changePasswordFields(true);
    } catch (error) {
      toast.error("OTP verification failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    const email = sessionStorage.getItem("user-email");

    if (!email) {
      toast.error("Email not found.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/forgot-password`,
        { email }
      );
      toast.success(response.data.message);
      setOtpFiled(true);
      setCodes({
        code1: "",
        code2: "",
        code3: "",
        code4: "",
        code5: "",
        code6: "",
      });
      setTimer(300); // Reset timer to 5 minutes
      startTimer(); // Start the countdown again
    } catch (error) {
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const passwordFormik = useFormik<ResetFormValues>({
    initialValues: { password: "", confirmPassword: "" },
    validate: (values) => {
      const errors: ResetFormErrors = {};
      if (!values.password) errors.password = "Password is required";
      if (!values.confirmPassword) {
        errors.confirmPassword = "Please confirm your password";
      } else if (values.password !== values.confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }
      return errors;
    },
    onSubmit: async (values) => {
      const email = sessionStorage.getItem("user-email");
      try {
        setIsLoading(true);
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/reset-password`,
          { email, newPassword: values.password }
        );

        if (response.status === 200) {
          toast.success(response.data.message);
          navigate("/login");
        }
        toast.success(response.data.message);
      } catch (error) {
        toast.error("Password reset failed.");
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <div className="login_bg_img flex justify-center items-center">
      <div className="w-screen h-screen flex justify-center items-center bg_blure">
        <div className="login_container w-[993px] h-[699px] flex items-center justify-center flex-col p-16">
          <div className="title flex items-center justify-center flex-col mt-[20px]">
            <h1 className="text-center text-white text-4xl font-semibold mb-[12px]">
              Forgot Password
            </h1>
            <p className="text-center text-white text-base max-w-[360px]">
              Please enter your registered email address to reset your password.
            </p>
          </div>

          <div className="details flex items-center justify-center mt-[40px]">
            {otpFiled ? (
              passwordFields ? (
                <>
                  <form onSubmit={passwordFormik.handleSubmit}>
                    <div className="mb-4">
                      <input
                        type="password"
                        className="email appearance-none bg-transparent focus:outline-none w-full py-2 px-3 leading-tight"
                        id="password"
                        placeholder="New Password"
                        {...passwordFormik.getFieldProps("password")}
                      />
                      {passwordFormik.errors.password && (
                        <div className="error p-2 text-red-500">
                          {passwordFormik.errors.password}
                        </div>
                      )}
                    </div>
                    <div className="mb-4">
                      <input
                        className="email appearance-none bg-transparent focus:outline-none w-full py-2 px-3 leading-tight"
                        type="password"
                        id="confirmPassword"
                        placeholder="Confirm Password"
                        {...passwordFormik.getFieldProps("confirmPassword")}
                      />
                      {passwordFormik.errors.confirmPassword && (
                        <div className="error p-2 text-red-500">
                          {passwordFormik.errors.confirmPassword}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-row items-center justify space-x-3">
                      <button
                        type="submit"
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-[24px] w-[200px] h-[48px]"
                      >
                        {isLoading ? (
                          <Loader isLoading={isLoading} />
                        ) : (
                          "Reset "
                        )}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <form className="max-w-sm mx-auto" onSubmit={handleOtpSubmit}>
                    <div className="text-[#fff] p-3 font-bold text-xl">
                      {" "}
                      {timer > 0 ? (
                        <div className="text-center text-sm">
                          OTP expires in: {formatTime(timer)}
                        </div>
                      ) : (
                        <div className="text-center text-sm">
                          OTP expired. Please resend.
                        </div>
                      )}{" "}
                    </div>
                    {/* show timer here  */}
                    <div className="flex mb-2 space-x-2 rtl:space-x-reverse">
                      {[
                        "code1",
                        "code2",
                        "code3",
                        "code4",
                        "code5",
                        "code6",
                      ].map((code, index) => (
                        <input
                          key={code}
                          type="text"
                          id={code}
                          maxLength={1}
                          value={codes[code] || ""}
                          onChange={handleInputChange}
                          className="block w-14 h-11 py-5 text-sm font-extrabold text-center text-gray-900 bg-white border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                          required
                        />
                      ))}
                    </div>
                    <p className="mt-2 text-sm text-center text-gray-200 dark:text-gray-400">
                      Please enter the 6-digit code we sent via email.
                    </p>
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg w-full mt-4"
                    >
                      {isLoading ? <Loader isLoading={isLoading} /> : "Verify"}
                    </button>

                    <p
                      className="text-blue-200 text-center p-2 underline cursor-pointer"
                      onClick={handleResendOtp}
                    >
                      Resent Otp
                    </p>
                  </form>
                </>
              )
            ) : (
              <form onSubmit={emailFormik.handleSubmit}>
                <div className="mb-4">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="email appearance-none bg-transparent focus:outline-none w-full py-2 px-3 leading-tight"
                    placeholder="Enter your email"
                    value={emailFormik.values.email}
                    onChange={emailFormik.handleChange}
                    onBlur={emailFormik.handleBlur}
                  />
                  {emailFormik.errors.email && (
                    <div className="text-red-500 p-2">
                      {emailFormik.errors.email}
                    </div>
                  )}
                </div>
                <div className="flex flex-row items-center justify space-x-3">
                  <button
                    type="button"
                    onClick={() => emailFormik.resetForm()}
                    className="text-white border font-bold py-2 px-4 rounded-[24px] w-[200px] h-[48px]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-[24px] w-[200px] h-[48px]"
                  >
                    {isLoading ? <Loader isLoading={isLoading} /> : "Send OTP"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
